import { Button, Col, Row, Spin, Upload } from "antd";
import { DocumentSection, DocumentView } from "components";
import {
  ErrorMessages,
  noDocumentMessageUpload,
  noDocumentSelectMessage,
  ProjectPermissionEnum
} from "constants/index";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { AttachmentDoc } from "models/submittal-log";
import { useContext, useEffect, useRef, useState } from "react";
import "./submittal-attachment.css";
import {
  IconLayoutSidebarRightCollapse,
  IconLayoutSidebarRightExpand
} from "@tabler/icons-react";

export type SubmittalAttachmentsProps = {
  documents: Array<AttachmentDoc>;
  referenceDocuments: Array<AttachmentDoc>;
  selectedDocument: AttachmentDoc | undefined;
  UploadAttachment: Function;
  RemoveAttachment: Function;
  setSelectedDocument: any;
  disabled: boolean;
  findLatestAttachmentVersion: (doc: AttachmentDoc) => AttachmentDoc;
  editableFields: any[];
  isIntegrationMode: boolean;
  addMore?: boolean;
};

function SubmittalAttachments({
  documents,
  referenceDocuments,
  disabled,
  selectedDocument,
  setSelectedDocument,
  UploadAttachment,
  RemoveAttachment,
  findLatestAttachmentVersion,
  editableFields,
  isIntegrationMode,
  addMore = false
}: SubmittalAttachmentsProps) {
  const [fileLoading, setFileLoading] = useState<boolean>(false);
  const buttonRef = useRef<HTMLElement>(null);

  const [fullScreen, setFullScreen] = useState(false);
  const { tokenContents } = useContext(ProjectContext);

  const toggleFullScreen = () => {
    setFullScreen((prev) => !prev);
  };
  const fileUploadRequest = async ({ file, onSuccess }: any) => {
    try {
      setFileLoading(true);
      await UploadAttachment({ file, onSuccess });
    } finally {
      setFileLoading(false);
    }
  };

  let documentView = (
    <div className="document-not-selected">{noDocumentMessageUpload}</div>
  );
  let expandToggleButton = null;

  if (documents.length > 0) {
    if (selectedDocument) {
      documentView = <DocumentView file={selectedDocument} />;
      expandToggleButton = (
        <Button
          size="small"
          className="p-0"
          style={{ position: "absolute", zIndex: 100, left: -3, top: "50%" }}
          onClick={toggleFullScreen}
          data-testid="index-button"
        >
          {fullScreen ? (
            <IconLayoutSidebarRightCollapse size={22} stroke={1.5} />
          ) : (
            <IconLayoutSidebarRightExpand size={22} stroke={1.5} />
          )}
        </Button>
      );
    } else {
      documentView = (
        <div className="document-not-selected">{noDocumentSelectMessage}</div>
      );
    }
  }

  const sidebarColSpan = fullScreen ? 0 : 6;
  const documentColSpan = 24 - sidebarColSpan;

  useEffect(() => {
    setTimeout(() => {
      if (addMore) {
        buttonRef.current?.click();
      }
    }, 500);
  }, [addMore]);

  return (
    <Row
      style={{ height: "calc(100vh - 206px)" }}
      className="p-4 !m-0 bg-white"
    >
      <Col span={sidebarColSpan}>
        <DocumentSection
          documents={documents}
          referenceDocuments={referenceDocuments}
          RemoveAttachment={RemoveAttachment}
          selectedDocument={selectedDocument!}
          setSelectedDocument={setSelectedDocument}
          findLatestAttachmentVersion={findLatestAttachmentVersion}
          disabled={
            !editableFields.includes("attachment") ||
            isPermissionNotGrantted(
              ProjectPermissionEnum.AddAttachment,
              tokenContents?.role!
            ) ||
            disabled
          }
        />
        <Upload
          showUploadList={false}
          customRequest={fileUploadRequest}
          className="upload-wrapper"
        >
          <Button
            ref={buttonRef}
            block
            title={(() => {
              if (!editableFields.includes("attachment"))
                return "Only the Ball In court user can currently add attachments";

              if (
                isPermissionNotGrantted(
                  ProjectPermissionEnum.AddAttachment,
                  tokenContents?.role!
                )
              )
                return ErrorMessages.PermissionNotGranted;

              return "";
            })()}
            disabled={
              isIntegrationMode ||
              !editableFields.includes("attachment") ||
              isPermissionNotGrantted(
                ProjectPermissionEnum.AddAttachment,
                tokenContents?.role!
              ) ||
              disabled
            }
          >
            {fileLoading ? <Spin size="small" /> : "Add"}
          </Button>
        </Upload>
      </Col>
      <Col span={documentColSpan}>
        {expandToggleButton}
        {documentView}
      </Col>
    </Row>
  );
}

export default SubmittalAttachments;
